import {
  InnerPageHeader, SectionCard, Card,
  Skeleton, SkeletonAvatar, SkeletonButton, SkeletonInput, SkeletonRegion,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '../../i18n';
import { STACK } from '../../constants/density';
import useResponsive from '../../hooks/useResponsive';
import { MobileHeader, MobileScreen } from '../../mobile';

/**
 * The role editor while its role loads — the numbered section cards, the
 * holders, the permission modules and the delete rail, in placeholder blocks.
 *
 * The router's Suspense fallback for `/people/roles/:id`, so it is imported
 * eagerly and stays light (kit, tokens, density, i18n, the mobile layout
 * library). The screen renders the same body inside its own header while
 * `usePeople` loads (`RoleEditorSkeletonWebBody`, `RoleEditorSkeletonMobileBody`),
 * because the header carries the back affordance and that must stay REAL — a
 * failed or slow load never traps the reader.
 */

const MODULES = 3;
const PERMS_PER_MODULE = 3;
const MEMBERS = 4;

/** One permission row: label + description on one side, a 44×24 switch on the other. */
function PermissionRowSkeleton({ i }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
        <Skeleton width={`${38 + ((i * 17) % 22)}%`} height={14} />
        <Skeleton width={`${55 + ((i * 11) % 25)}%`} height={12} />
      </div>
      <Skeleton width={44} height={24} radius={RADIUS.pill} />
    </div>
  );
}

/** The holders row: `MEMBERS` avatar-26 + name pairs. */
function MembersSkeleton({ size = 26 }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {Array.from({ length: MEMBERS }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <SkeletonAvatar size={size} />
          <Skeleton width={64 + ((i * 19) % 40)} height={13} />
        </div>
      ))}
    </div>
  );
}

/** The desktop body: the two-column grid under the header. */
export function RoleEditorSkeletonWebBody() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="role-editor-skeleton-body"
      className="role-editor-grid"
      style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 18, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SectionCard n={1} title={t('people.roleEditor.details')}>
          <SkeletonInput />
          <SkeletonInput multiline />
        </SectionCard>

        <SectionCard n={2} title={t('people.roleEditor.members')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
            <MembersSkeleton />
          </div>
          <div style={{ marginTop: STACK.regular }}>
            <SkeletonButton width={160} />
          </div>
        </SectionCard>

        {Array.from({ length: MODULES }, (_, m) => (
          <SectionCard key={m} n={m + 3} title={<Skeleton width={96 + (m % 2) * 28} height={16} />}>
            {Array.from({ length: PERMS_PER_MODULE }, (_, i) => <PermissionRowSkeleton key={i} i={m * PERMS_PER_MODULE + i} />)}
          </SectionCard>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SectionCard n="!" title={t('people.actions.deleteRole')}>
          <Skeleton width="90%" height={13} style={{ marginBottom: 8 }} />
          <SkeletonButton width={140} />
        </SectionCard>
      </div>
    </SkeletonRegion>
  );
}

/** The whole desktop page: a real InnerPageHeader (title + back) over the body. */
export function RoleEditorSkeletonWeb() {
  const { t } = useTranslation();
  return (
    <div data-state="loading" data-testid="role-editor-skeleton"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg }}>
      <style>{`
        @media (max-width: 1180px) {
          .role-editor-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <InnerPageHeader
        backLabel={t('common.actions.back')}
        title={t('people.entity.role')}
        sub={t('people.roleEditor.subtitle')}
        onBack={() => window.history.back()}
      />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 32 }}>
        <RoleEditorSkeletonWebBody />
      </div>
    </div>
  );
}

/** One collapsed `MobilePermissionGroup`: the 44px header row — title, count, chevron. */
function PermissionGroupSkeleton({ i }) {
  return (
    <div style={{
      minHeight: 44, padding: '14px 4px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Skeleton width={88 + ((i * 23) % 40)} height={14} />
        <Skeleton width={22} height={12} />
      </div>
      <Skeleton width={16} height={16} />
    </div>
  );
}

/** The mobile body: the fields, the holders card, the search, the grouped permissions. */
export function RoleEditorSkeletonMobileBody() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="role-editor-skeleton-body"
      style={{ padding: '1rem', display: 'grid', gap: `${STACK.regular}px` }}>
      <SkeletonInput />
      <SkeletonInput multiline />

      <Card aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: `${STACK.tight}px` }}>
        <Skeleton width={120} height={15} />
        <MembersSkeleton />
        <div>
          <SkeletonButton width={168} />
        </div>
      </Card>

      <SkeletonInput label={false} />

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: '0.25rem 0.75rem',
      }}>
        {Array.from({ length: MODULES }, (_, i) => <PermissionGroupSkeleton key={i} i={i} />)}
      </div>

      <SkeletonButton width="100%" />
    </SkeletonRegion>
  );
}

/** The whole mobile page: a real header band (title + back) over the body. */
export function RoleEditorSkeletonMobile() {
  const { t } = useTranslation();
  const header = (
    <MobileHeader
      title={t('people.entity.role')}
      subtitle={t('people.roleEditor.subtitle')}
      onBack={() => window.history.back()}
      backLabel={t('common.actions.back')}
      data-testid="role-editor-header"
    />
  );
  return (
    <MobileScreen header={header} data-state="loading" data-testid="role-editor-skeleton">
      <RoleEditorSkeletonMobileBody />
    </MobileScreen>
  );
}

export default function RoleEditorSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <RoleEditorSkeletonMobile {...props} /> : <RoleEditorSkeletonWeb {...props} />;
}
